import React, { useState, useEffect } from 'react';
import { Modal, Button, Typography } from 'antd';
import { DownloadOutlined, AppstoreOutlined } from '@ant-design/icons';
import './InstallPWAPrompt.css';

const { Title, Text } = Typography;

/**
 * Component to prompt users to install the PWA on their mobile device
 * Checks if the app is already installed (running in standalone mode)
 */
const InstallPWAPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);

  // Check if the app is running in standalone mode (installed)
  const isAppInstalled = () => {
    return window.matchMedia('(display-mode: standalone)').matches || 
           window.navigator.standalone === true;
  };

  // Check if the device is iOS
  const checkIsIOS = () => {
    const ua = window.navigator.userAgent;
    return /iPhone|iPad|iPod/.test(ua) && !window.MSStream;
  };

  useEffect(() => {
    // Only show on mobile devices
    const isMobileDevice = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );

    if (!isMobileDevice) return;

    setIsIOS(checkIsIOS());

    // Don't show the prompt if the app is already installed
    if (isAppInstalled()) {
      console.log('App is already installed');
      return;
    }

    // Show prompt after a delay
    const timer = setTimeout(() => {
      setShowPrompt(true);
    }, 3000);

    // Handle the beforeinstallprompt event (for non-iOS devices)
    window.addEventListener('beforeinstallprompt', (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
      // Show the prompt
      setShowPrompt(true);
    });

    return () => {
      clearTimeout(timer);
      window.removeEventListener('beforeinstallprompt', () => {});
    };
  }, []);

  const handleInstallClick = async () => {
    if (isIOS) {
      // Can't automatically install on iOS, just close the prompt
      setShowPrompt(false);
    } else if (deferredPrompt) {
      // Show the install prompt
      deferredPrompt.prompt();
      
      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`User response to the install prompt: ${outcome}`);
      
      // We no longer need the prompt
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  };

  const handleClose = () => {
    setShowPrompt(false);
    // Store in localStorage that user dismissed the prompt
    localStorage.setItem('pwaPromptDismissed', 'true');
  };

  // Don't render anything if the prompt shouldn't be shown
  if (!showPrompt || isAppInstalled() || localStorage.getItem('pwaPromptDismissed') === 'true') {
    return null;
  }

  return (
    <Modal
      open={showPrompt}
      onCancel={handleClose}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          Maybe Later
        </Button>,
        <Button 
          key="install" 
          type="primary" 
          icon={<DownloadOutlined />} 
          onClick={handleInstallClick}
        >
          {isIOS ? 'Got It' : 'Install App'}
        </Button>
      ]}
      title={
        <div className="pwa-prompt-title">
          <AppstoreOutlined className="pwa-prompt-icon" />
          <span>Install Mobile App</span>
        </div>
      }
    >
      <div className="pwa-prompt-content">
        <Text>
          {isIOS 
            ? 'To install this app on your iOS device follow the steps below.' 
            : 'To install this app on your device please click the install below.'}
        </Text>
        
        {isIOS && (
          <div className="ios-instructions">
            <div className="step">
              <div className="step-number">1</div>
              <div className="step-text">Tap the share icon</div>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <div className="step-text">Scroll down and tap "Add to Home Screen"</div>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <div className="step-text">Tap "Add" in the top right</div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default InstallPWAPrompt;
